<template>
  <div>
    <div class="flex border border-ink">
      <div
        class="flex-1 text-center py-3 cursor-pointer typeset-4"
        @click="openFiltersModal">
        {{ t("category.filters.title") }}
        <span
          v-if="selectedOptionsCount > 0"
          class="inline-flex">
          {{ buttonFilterCount }}
        </span>
      </div>

      <label
        v-if="canSort"
        class="flex-1 text-center py-3 cursor-pointer border-l border-ink relative">
        <div class="absolute top-0 right-0 botom-0 left-0 py-3 typeset-4">
          Sort By
        </div>
        <select
          id="sort-by"
          v-model="sortBy"
          name="sort-by"
          class="absolute top-0 right-0 botom-0 left-0 w-full h-full opacity-0"
          @change="handleChange">
          <option
            v-for="sort in availableSorts"
            :key="sort"
            :value="sort">
            {{
              t("category.sort.dropdown-value", {
                value: t(`category.sort.${sort}`)
              })
            }}
          </option>
        </select>
      </label>
    </div>
    <teleport to="#applied-mobile-filters">
      <div
        v-if="selectedOptionsCount > 0"
        class="flex flex-wrap gap-3 mb-3">
        <template v-for="type in filtersForEditing">
          <div
            v-for="(option, index) in visibleOptions(type)"
            :key="`${type.key}-${index}`"
            class="btn btn-secondary flex items-center px-3">
            {{
              option.name == "true"
                ? t(`search.mobile.refine-modal.refine-by.photo-description`)
                : option.name
            }}
            <div
              class="-mt-1 ml-3 flex items-center justify-center"
              @click="
                option.active = false;
                handleChange();
              ">
              <svg-icon
                name="cross"
                height="9px"
                width="9px" />
            </div>
          </div>
        </template>
        <a
          href="#"
          class="btn btn-link px-3"
          @click.prevent="clearAllFilters()">
          {{ t("search.mobile.refine-modal.clear-all") }}
        </a>
      </div>
    </teleport>

    <teleport to="#end-of-body">
      <modal
        key="mobileFiltersModal"
        :open="modalOpen"
        :class="{ 'is-in-background': !!openModal }"
        class="filters-modal new-filters-modal"
        header-class="modal-header bg-paper-1"
        footer-class="modal-footer bg-paper-1"
        size="small"
        edge-to-edge
        @closed="cancelFiltersModal">
        <template #header>
          <h3 class="typeset-4">
            {{ t("search.mobile.refine-modal.title") }}
            <template v-if="selectedOptionsCount">
              ({{ selectedOptionsCount }})
            </template>
          </h3>
        </template>
        <template #close-icon>
          <svg-icon
            name="close"
            width="16px"
            height="16px" />
        </template>
        <template #body>
          <div class="-m-4">
            <div
              v-for="filter in filtersForEditing"
              :key="filter.key"
              class="mb-4">
              <div
                class="flex items-center justify-between p-4"
                @click="toggleFilter(filter.key)">
                <span class="typeset-4">
                  {{ filter.title }}
                </span>
                <svg-icon
                  name="downarrow-small"
                  :class="{ 'rotate-180': filterTypeShouldBeOpen(filter.key) }"
                  class="flex-no-shrink" />
              </div>
              <slide-transition>
                <div
                  v-if="filterTypeShouldBeOpen(filter.key)"
                  class="px-4 grid grid-cols-2 gap-3">
                  <div
                    v-for="(option, index) in sortedOptions(filter.options)"
                    :key="index">
                    <checkbox-input
                      v-model:checked="option.active"
                      :input-id="`${filter.key}-${index}`"
                      :label="
                        filter.key === 'photo'
                          ? t(
                              `search.mobile.refine-modal.refine-by.photo-description`
                            )
                          : option.name
                      " />
                  </div>
                </div>
              </slide-transition>
            </div>
          </div>
        </template>
        <template #footer>
          <div class="flex gap-4">
            <button
              class="flex-1 btn btn-secondary"
              :disabled="selectedOptionsCount === 0 ? 'disabled' : null"
              @click="clearAllFilters()">
              {{ t("search.mobile.refine-modal.clear-all") }}
            </button>
            <btn
              type="primary"
              class="flex-1"
              :disabled="!filtersHaveChanged"
              @click="handleApply">
              {{ t("search.mobile.refine-modal.apply") }}
              <template v-if="selectedOptionsCount">
                ({{ selectedOptionsCount }})
              </template>
            </btn>
          </div>
        </template>
      </modal>
    </teleport>
  </div>
</template>

<script>
import { cloneDeep, flatten, keyBy, mapValues, isEqual } from "lodash-es";
import applyFilters from "../modules/applyFilters";
import SlideTransition from "../components/transitions/SlideTransition.vue";
import Btn from "../components/shared/Btn.vue";
import CheckboxInput from "../components/shared/CheckboxInput.vue";
import Modal from "../components/shared/Modal.vue";
import SvgIcon from "../components/shared/SvgIcon.vue";

export default {
  name: "MobileFilters",
  components: {
    Btn,
    CheckboxInput,
    Modal,
    SvgIcon,
    SlideTransition
  },
  props: {
    query: {
      type: String,
      default: null
    },
    new: {
      type: String,
      default: null
    },
    filters: {
      type: Array,
      required: true
    },
    canSort: {
      type: Boolean,
      default: false
    },
    availableSorts: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      modalOpen: false,
      openModal: "",
      filtersForEditing: {},
      filtersWhenModalWasOpened: {},
      sortBy: "favourite",
      openFilterTypes: []
    };
  },
  computed: {
    secondaryModalOpen() {
      return filterKey => this.openModal === filterKey;
    },
    selectedOptions() {
      return mapValues(keyBy(this.filtersForEditing, "key"), obj =>
        obj.options.filter(option => option.active).map(option => option.value)
      );
    },
    selectedOptionsCount() {
      return flatten(Object.values(this.selectedOptions)).length;
    },
    buttonFilterCount() {
      return `(${this.selectedOptionsCount})`;
    },
    individualCount() {
      return filter => filter.options.filter(option => option.active).length;
    },
    visibleOptions() {
      return type => {
        return type.options.filter(option => option.active);
      };
    },
    abTestRef() {
      return this.$refs.abTest63;
    },
    filterTypeShouldBeOpen() {
      return filterKey => this.openFilterTypes.includes(filterKey);
    },
    filtersHaveChanged() {
      return !isEqual(this.filtersWhenModalWasOpened, this.filtersForEditing);
    },
    sortedOptions() {
      return options => {
        return [...options].sort((a, b) => {
          return a.name.localeCompare(b.name);
        });
      };
    }
  },
  mounted() {
    // Use filtersForEditing as the starting point,
    // as we shouldn't mutate the filters prop
    this.filtersForEditing = cloneDeep(this.filters);
    window.addEventListener("mobileFiltersUpdated", this.updateFilters);
  },
  methods: {
    openFiltersModal() {
      this.filtersWhenModalWasOpened = cloneDeep(this.filtersForEditing);
      if (!this.openFiltersSet) {
        this.openFilterTypes = this.filtersForEditing
          .slice(0, 4)
          .map(filter => filter.key);
      }
      this.openFiltersSet = true;
      document.documentElement.classList.add("mobile-filters-modal-open");
      this.preventScrolling();
      this.modalOpen = true;
    },
    preventScrolling() {
      document.documentElement.classList.add("no-scroll");
      document.body.classList.add("no-scroll");
    },
    allowScrolling() {
      document.documentElement.classList.remove("no-scroll");
      document.body.classList.remove("no-scroll");
    },
    hideFiltersModal() {
      this.modalOpen = false;
      document.documentElement.classList.remove("mobile-filters-modal-open");
      this.allowScrolling();
    },
    cancelFiltersModal() {
      if (this.filtersHaveChanged) {
        this.filtersForEditing = cloneDeep(this.filtersWhenModalWasOpened);
      }
      this.hideFiltersModal();
    },
    toggleFilter(filterKey) {
      this.openFilterTypes = this.openFilterTypes.includes(filterKey)
        ? this.openFilterTypes.filter(filter => filter !== filterKey)
        : [...this.openFilterTypes, filterKey];
    },
    clearAllFilters() {
      this.filtersForEditing.forEach(type => {
        type.options.forEach(opt => {
          const newOpt = opt;
          newOpt.active = false;
        });
      });
      this.handleChange();
    },
    handleApply() {
      this.hideFiltersModal();
      this.handleChange();
    },
    handleChange() {
      const hash = this.query ? { q: this.query } : {};
      this.filtersForEditing.forEach(filter => {
        const selectedOptions = filter.options.filter(option => option.active);
        if (selectedOptions.length) {
          if (filter.param === "photo") {
            hash[`${filter.param}`] = selectedOptions.map(
              option => option.value
            );
          } else {
            hash[`${filter.param}[]`] = selectedOptions.map(
              option => option.value
            );
          }
        }
      });
      hash.sort = this.sortBy;
      const filtersFormElement = document.querySelector(".filters");
      applyFilters(
        hash,
        filtersFormElement.getAttribute("action"),
        !!filtersFormElement.dataset.addToPath
      );
    },
    updateFilters(e) {
      this.filtersForEditing = e.detail;
    }
  }
};
</script>

<style lang="scss" scoped>
.icon-downarrow-small {
  transition: transform 0.2s ease-in-out;

  &.rotate-180 {
    transform: rotateX(180deg);
  }
}
</style>
